/**
 * getUserMedia as a hook, with the permission states the setup flow has to
 * distinguish between: "not asked yet", "asked and denied", "no camera at all"
 * and "granted". A student who denied the prompt needs different instructions
 * from one whose laptop has no webcam, so these are not collapsed into "error".
 *
 * Recovery is the hard part on a real phone. The user often lands on the blocked
 * screen, THEN grants permission (in the address-bar prompt, or in Settings and
 * back), and the page has to notice and start the camera without a reload. Three
 * things drive that here:
 *   - a gesture-driven `request()` the blocked screen wires to a button;
 *   - the Permissions API `change` event, so a grant made anywhere flips us to
 *     ready on its own;
 *   - a visibility/focus re-check, for browsers (iOS Safari) whose Permissions
 *     API does not report `camera`, where returning to the tab is the only
 *     signal we get.
 *
 * Constraints are requested in tiers. `facingMode: "environment"` is an *ideal*,
 * not `{ exact }`, so it never throws OverconstrainedError by itself — but the
 * width/height/frameRate hints on a cheap phone can, so a failed tier falls back
 * to a looser one rather than surfacing as a scary "blocked" state.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { attachMic, hasLiveAudio, mediaTiers } from "./mediaStreams";

export type CameraState =
  "idle" | "requesting" | "ready" | "denied" | "unavailable" | "error";

/**
 * The microphone, tracked SEPARATELY from the camera.
 *
 * It has to be separate because it fails separately: the browser can grant the
 * camera and refuse the microphone in the same call, and the fallback below
 * hides that by design — a missing mic must never cost the student their video.
 * Hiding it from the CODE too is how you end up with a room recording that has
 * no sound and nobody finding out until someone tries to listen to it.
 *
 * `idle` is the honest state after a combined request that came back with no
 * audio track: the reason is not knowable from that call (denied? no mic?),
 * and asking again, alone, is what produces an answer.
 */
export type MicState =
  "idle" | "requesting" | "on" | "denied" | "unavailable" | "error";

export interface CameraDiagnostics {
  /** The DOMException name of the last failure, e.g. "NotAllowedError". */
  errorName: string | null;
  /** Its message, verbatim. */
  errorMessage: string | null;
  /** getUserMedia only works in a secure context; false here explains a lot. */
  secureContext: boolean;
  /** Absent in some in-app webviews — worth knowing before blaming permission. */
  hasMediaDevices: boolean;
  /**
   * How many getUserMedia calls have been made. 0 means we are still waiting on
   * the first gesture; 1 means the failure is on the very first call (before any
   * retry); >1 means a retry has already failed. This is the fresh-vs-retry
   * signal a bug report needs.
   */
  attempts: number;
}

export interface UseCameraOptions {
  /** "user" for the laptop webcam, "environment" for the phone's rear camera. */
  facingMode?: "user" | "environment";
  audio?: boolean;
  /** Modest by default: enough to identify a face, cheap to encode and upload. */
  width?: number;
  height?: number;
  /** Do not open the camera until this is true. */
  enabled?: boolean;
  /**
   * Whether to call getUserMedia automatically on mount. Defaults to true, which
   * is right on a laptop where an ungestured call simply shows the prompt.
   *
   * Set FALSE on a phone. On Android Chrome an ungestured getUserMedia on mount
   * can be auto-dismissed and, worse, persist a "blocked" decision for the site
   * — after which every later call rejects with NotAllowedError and the student
   * cannot recover without clearing site settings. With this false the camera
   * opens only from the explicit "Enable camera" tap, which is a real gesture.
   */
  autoRequest?: boolean;
}

export interface CameraHandle {
  stream: MediaStream | null;
  state: CameraState;
  errorMessage: string | null;
  /** Raw failure details for on-screen diagnostics and bug reports. */
  diagnostics: CameraDiagnostics;
  /** Attach to a <video> element; playback is started for you. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  request: () => Promise<MediaStream | null>;
  /**
   * Ask for the microphone ON ITS OWN and attach it to the live stream.
   *
   * This is the recovery path for the common desktop case: the student allowed
   * the camera and dismissed or refused the microphone, and now wants it on
   * without being asked for the camera a second time.
   */
  requestMic: () => Promise<boolean>;
  /** Whether the stream is carrying a LIVE microphone track right now. */
  hasAudio: boolean;
  micState: MicState;
  micErrorMessage: string | null;
  stop: () => void;
}

/** Whether another getUserMedia attempt could plausibly succeed. */
function isRecoverable(state: CameraState): boolean {
  return state === "idle" || state === "denied" || state === "error";
}

export function useCamera(options: UseCameraOptions = {}): CameraHandle {
  const {
    facingMode = "user",
    audio = false,
    width = 640,
    height = 480,
    enabled = true,
    autoRequest = true,
  } = options;

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [state, setState] = useState<CameraState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorName, setErrorName] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [micState, setMicState] = useState<MicState>("idle");
  const [micErrorMessage, setMicErrorMessage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Guards against overlapping getUserMedia calls (button + auto-recovery). */
  const requestingRef = useRef(false);
  /** The same guard for the microphone-only call. */
  const requestingMicRef = useRef(false);
  /**
   * True once the student has made an explicit request. The visibility/focus
   * auto-retry keys off this so it never fires an ungestured call on first load
   * — only recovers a request the student has already initiated.
   */
  const hasRequestedRef = useRef(false);
  /**
   * True while WE are ending the stream on purpose (stop / unmount), so the
   * track's `ended` event is not mistaken for the camera dying and does not
   * trigger a re-acquire.
   */
  const teardownRef = useRef(false);
  /** Latest state, readable from listeners that must not re-subscribe. */
  const stateRef = useRef<CameraState>(state);
  stateRef.current = state;

  const stop = useCallback(() => {
    teardownRef.current = true;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    setState("idle");
  }, []);

  const request = useCallback(async (): Promise<MediaStream | null> => {
    // Reuse the current stream only if it is still LIVE. A dead track here means
    // the camera was lost and we are being asked to recover — fall through and
    // acquire a fresh one instead of handing back a stream that produces nothing.
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track && track.readyState === "live") return streamRef.current;
      streamRef.current = null;
    }
    if (requestingRef.current) return null;

    if (!navigator.mediaDevices?.getUserMedia) {
      setState("unavailable");
      setErrorName("NoMediaDevices");
      setErrorMessage(
        window.isSecureContext
          ? "This browser cannot access a camera. If you are inside another app, open the page in Chrome or Safari directly."
          : "The camera is only available over HTTPS. Open the https:// address, not a plain http one.",
      );
      return null;
    }

    requestingRef.current = true;
    hasRequestedRef.current = true;
    setAttempts((n) => n + 1);
    setState("requesting");
    setErrorName(null);
    setErrorMessage(null);

    // Progressively looser constraints, ending in one that asks for no audio
    // at all — a blocked microphone must never cost us the camera. See
    // `mediaTiers`, where that rule lives and is tested.
    const tiers = mediaTiers({ facingMode, width, height, audio });

    try {
      let lastErr: unknown = null;
      for (const constraints of tiers) {
        try {
          const media = await navigator.mediaDevices.getUserMedia(constraints);
          teardownRef.current = false;
          streamRef.current = media;
          setStream(media);
          setState("ready");
          // Which TIER succeeded is the only thing that tells us whether the
          // microphone came with it — the loop below falls back to video-only
          // silently, and that silence is the whole point of this line.
          if (audio) {
            setMicState(media.getAudioTracks().length > 0 ? "on" : "idle");
          }
          setErrorName(null);
          setErrorMessage(null);
          return media;
        } catch (err) {
          // Try the next tier for ANY failure. Because the tiers relax both the
          // video constraints and the audio requirement, only a genuine camera
          // denial or absence fails every tier — and then the final, most
          // permissive tier's error is the one we classify, so we never blame a
          // missing mic for a working camera.
          lastErr = err;
        }
      }
      throw lastErr;
    } catch (err) {
      const name = err instanceof DOMException ? err.name : "Error";
      const message = err instanceof Error ? err.message : String(err);
      setErrorName(name);
      setErrorMessage(null); // set specifically below

      if (name === "NotAllowedError" || name === "SecurityError") {
        setState("denied");
        setErrorMessage(
          "Camera access is blocked for this page. Allow it (tap the camera icon in the address bar, or your phone's Settings), then tap “Enable camera”.",
        );
      } else if (
        name === "NotFoundError" ||
        name === "OverconstrainedError" ||
        name === "DevicesNotFoundError"
      ) {
        setState("unavailable");
        setErrorMessage("No usable camera was found on this device.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setState("error");
        setErrorMessage(
          "The camera could not start — another app may be using it. Close other camera apps and try again.",
        );
      } else {
        setState("error");
        setErrorMessage(message || "Could not open the camera.");
      }
      return null;
    } finally {
      requestingRef.current = false;
    }
  }, [facingMode, audio, width, height]);

  /**
   * The microphone, asked for on its own and spliced into the live stream.
   *
   * The camera is NOT re-requested: the student is asked for the microphone
   * alone, the picture never blinks, and a refusal here cannot cost them the
   * video they already have. `attachMic` does the splicing, and carries the
   * reason it must hand back a whole new stream.
   */
  const requestMic = useCallback(async (): Promise<boolean> => {
    if (requestingMicRef.current) return false;

    const current = streamRef.current;
    if (!current) return false;
    if (hasLiveAudio(current)) {
      setMicState("on");
      return true;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setMicState("unavailable");
      setMicErrorMessage(
        window.isSecureContext
          ? "This browser cannot access a microphone."
          : "The microphone is only available over HTTPS.",
      );
      return false;
    }

    requestingMicRef.current = true;
    setMicState("requesting");
    setMicErrorMessage(null);

    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      const track = mic.getAudioTracks()[0];
      if (!track) {
        mic.getTracks().forEach((t) => t.stop());
        setMicState("unavailable");
        setMicErrorMessage("No microphone was found on this device.");
        return false;
      }

      teardownRef.current = false;
      const next = attachMic(current, track);
      streamRef.current = next;
      setStream(next);
      setMicState("on");
      setMicErrorMessage(null);
      return true;
    } catch (err) {
      const name = err instanceof DOMException ? err.name : "Error";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setMicState("denied");
        setMicErrorMessage(
          "Microphone access is blocked for this page. Allow it from the icon in the address bar, then try again.",
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setMicState("unavailable");
        setMicErrorMessage("No microphone was found on this device.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setMicState("error");
        setMicErrorMessage(
          "The microphone could not start — another app may be using it.",
        );
      } else {
        setMicState("error");
        setMicErrorMessage(
          err instanceof Error ? err.message : "Could not open the microphone.",
        );
      }
      return false;
    } finally {
      requestingMicRef.current = false;
    }
  }, []);

  // First attempt on mount — laptop only. On the phone (`autoRequest: false`)
  // the camera opens from the explicit "Enable camera" tap instead, so we never
  // make an ungestured call that Android Chrome could turn into a persisted
  // "blocked" decision.
  useEffect(() => {
    if (enabled && autoRequest) void request();
  }, [enabled, autoRequest, request]);

  // Auto-recover when permission is granted after we have already failed.
  // The Permissions API is the reliable signal where it exists (Android Chrome);
  // it fires `change` the moment the user allows the camera, even from Settings.
  useEffect(() => {
    if (!enabled) return;
    const perms = navigator.permissions;
    if (!perms?.query) return;

    let status: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => {
      if (status?.state === "granted" && isRecoverable(stateRef.current)) {
        void request();
      }
    };

    // `camera` is not in the TS PermissionName union on every lib version, and
    // some browsers reject the query outright — both are handled by the catch.
    perms
      .query({ name: "camera" as PermissionName })
      .then((s) => {
        if (cancelled) return;
        status = s;
        s.addEventListener("change", onChange);
      })
      .catch(() => {
        /* Permissions API without `camera` (iOS Safari) — visibility covers it */
      });

    return () => {
      cancelled = true;
      status?.removeEventListener("change", onChange);
    };
  }, [enabled, request]);

  // Fallback recovery for browsers with no `camera` permission query: when the
  // user comes back to the tab (from Settings, from the permission sheet), try
  // again if we are still in a recoverable state. Returning to the tab is also a
  // user gesture, which iOS Safari requires.
  useEffect(() => {
    if (!enabled) return;
    const retry = () => {
      if (
        hasRequestedRef.current &&
        document.visibilityState === "visible" &&
        isRecoverable(stateRef.current)
      ) {
        void request();
      }
    };
    document.addEventListener("visibilitychange", retry);
    window.addEventListener("focus", retry);
    return () => {
      document.removeEventListener("visibilitychange", retry);
      window.removeEventListener("focus", retry);
    };
  }, [enabled, request]);

  // Bind the stream to the <video> whenever either changes.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    void el.play().catch(() => {
      /* autoplay can be refused until a gesture; the poster frame still shows */
    });
  }, [stream]);

  // Self-heal: if the live video track ENDS on its own — the OS reclaimed the
  // camera, a device glitch, another app grabbed it — re-acquire so recording
  // does not silently die for the rest of the exam. This is the laptop-webcam
  // failure mode: the track ended mid-exam and nothing brought it back. We
  // ignore ends that WE caused (stop / unmount), tracked by `teardownRef`.
  useEffect(() => {
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (!track) return;

    const onEnded = () => {
      if (teardownRef.current) return; // we ended it on purpose
      if (streamRef.current !== stream) return; // already replaced
      streamRef.current = null;
      setStream(null);
      setState("idle");
      if (enabled) void request();
    };

    track.addEventListener("ended", onEnded);
    return () => track.removeEventListener("ended", onEnded);
  }, [stream, enabled, request]);

  // Auto-attach the microphone when permission is granted AFTER the camera is
  // already running — the student allows it from the address bar and expects
  // the app to notice. `requestMic` is a no-op once there is a live audio
  // track, so a `change` event that fires for anything else costs nothing.
  useEffect(() => {
    if (!enabled || !audio) return;
    const perms = navigator.permissions;
    if (!perms?.query) return;

    let status: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => {
      if (status?.state === "granted") void requestMic();
    };

    perms
      .query({ name: "microphone" as PermissionName })
      .then((s) => {
        if (cancelled) return;
        status = s;
        s.addEventListener("change", onChange);
      })
      .catch(() => {
        /* no `microphone` permission query here — the button still works */
      });

    return () => {
      cancelled = true;
      status?.removeEventListener("change", onChange);
    };
  }, [enabled, audio, requestMic]);

  // A microphone that ENDS mid-exam — a headset unplugged, the OS handing the
  // device to another app — is the silent-recording failure again, and the only
  // notice of it is this event. Reflected in state so the UI stops claiming the
  // room is being heard. The camera is deliberately left alone: video is the
  // recording that matters, and it is still fine.
  useEffect(() => {
    if (!stream) return;
    const track = stream.getAudioTracks()[0];
    if (!track) return;

    const onEnded = () => {
      if (teardownRef.current) return;
      setMicState("idle");
      setMicErrorMessage(null);
    };

    track.addEventListener("ended", onEnded);
    return () => track.removeEventListener("ended", onEnded);
  }, [stream]);

  // Release the camera when the component goes away — the light staying on
  // after the exam is both alarming and a genuine privacy problem.
  useEffect(() => {
    return () => {
      teardownRef.current = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  return {
    stream,
    state,
    errorMessage,
    diagnostics: {
      errorName,
      errorMessage,
      secureContext:
        typeof window !== "undefined" && window.isSecureContext === true,
      hasMediaDevices:
        typeof navigator !== "undefined" &&
        !!navigator.mediaDevices?.getUserMedia,
      attempts,
    },
    videoRef,
    request,
    requestMic,
    hasAudio: hasLiveAudio(stream),
    micState,
    micErrorMessage,
    stop,
  };
}

/** Smallest downscale that still produces a recognisable picture. */
const MIN_LONG_EDGE = 16;

/**
 * Grabs a still frame from a live video element as a JPEG blob.
 *
 * `longEdge` downscales, preserving aspect ratio, and never upscales — a
 * viewfinder frame wants to be a few kilobytes, while a room-scan still wants
 * the sensor's full resolution. Omit it for full size.
 */
export async function captureStill(
  video: HTMLVideoElement,
  quality = 0.85,
  longEdge?: number,
): Promise<Blob | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;

  // Clamped rather than ignored. Treating a small `longEdge` as "no downscale"
  // silently returns a full-resolution frame to a caller that asked for a
  // thumbnail — the opposite of what they wanted, and invisible until
  // something chokes on the size.
  const scale =
    longEdge === undefined
      ? 1
      : Math.min(
          1,
          Math.max(MIN_LONG_EDGE, longEdge) / Math.max(width, height),
        );

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}
